import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { isNativeApp } from '../lib/native/platform'

/**
 * [Native B1] An `<img src>` for an authenticated `/api/...` image (portraits).
 *
 * Web: the url unchanged — the browser sends the session cookie with the image request.
 * Native: an `<img>` cannot carry the bearer credential, and a relative `/api` URL would resolve
 * against the app shell, so the image is fetched through `api` (instance base URL + bearer) as a
 * blob and shown via an object URL, revoked when the url changes or the component unmounts.
 * Until it loads, or if it fails, this is undefined (avatars fall back to initials). A url that is
 * not under `/api/` (a local preview blob/data URL) passes through untouched. `/api//host/…` is
 * NOT under `/api/` — stripped of `/api` it would be a protocol-relative URL to another host.
 */
const AUTHED_API_PATH = /^\/api\/[^/\\]/

export function useAuthedSrc(url: string | null | undefined): string | undefined {
  const fetchOnNative = isNativeApp() && !!url && AUTHED_API_PATH.test(url)
  const [blob, setBlob] = useState<{ url: string; objectUrl: string } | null>(null)

  useEffect(() => {
    if (!fetchOnNative || !url) return
    let cancelled = false
    let objectUrl: string | null = null
    api
      .get<Blob>(url.slice('/api'.length), { responseType: 'blob' })
      .then(({ data }) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(data)
        setBlob({ url, objectUrl })
      })
      .catch(() => {
        // Missing / forbidden / offline: no image, the caller's fallback renders.
      })
    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        // Forget the revoked URL too, or A→B→A would hand back A's dead blob: URL until A refetched.
        const revoked = objectUrl
        setBlob((b) => (b?.objectUrl === revoked ? null : b))
      }
    }
  }, [fetchOnNative, url])

  if (!url) return undefined
  if (!fetchOnNative) return url
  return blob?.url === url ? blob.objectUrl : undefined
}
